import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const BookingPage: React.FC = () => {
  const [iframeError, setIframeError] = React.useState(false);

  useEffect(() => {
    // Load TidyCal script
    const script = document.createElement('script');
    script.src = 'https://asset-tidycal.b-cdn.net/js/embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Torna alla home
          </Link>

          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Prenota la Tua Call Strategica Gratuita
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Parla con un nostro esperto di AI e scopri come possiamo aiutarti a implementare
              soluzioni intelligenti nel tuo business.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {/* Benefits Column */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Cosa Include la Call
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Analisi Personalizzata</p>
                    <p className="text-sm text-gray-600">Review del tuo AI Readiness Report e raccomandazioni specifiche</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Roadmap Dettagliata</p>
                    <p className="text-sm text-gray-600">Piano di implementazione step-by-step personalizzato</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Q&A con Esperti</p>
                    <p className="text-sm text-gray-600">Risposte a tutte le tue domande su AI e automazione</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Budget & ROI</p>
                    <p className="text-sm text-gray-600">Analisi dettagliata dei costi e del ritorno sull'investimento</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-primary-50 rounded-lg p-6 border border-primary-200">
              <div className="flex items-center mb-3">
                <ClockIcon className="h-5 w-5 text-primary-600 mr-2" />
                <h3 className="font-semibold text-primary-900">Durata: 30 minuti</h3>
              </div>
              <p className="text-sm text-primary-700">
                Una sessione focalizzata per massimizzare il valore e rispondere a tutte le tue domande.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <div className="flex items-center mb-3">
                <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="font-semibold text-blue-900">100% Gratuita</h3>
              </div>
              <p className="text-sm text-blue-700">
                Nessun impegno richiesto. La call è completamente gratuita e senza obblighi.
              </p>
            </div>
          </div>

          {/* TidyCal Embed Column */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Seleziona Data e Orario
                </h2>
                <p className="text-gray-600">
                  Scegli il momento migliore per te
                </p>
              </div>

              {/* TidyCal Embed */}
              <div className="relative">
                {!iframeError ? (
                  <iframe
                    src="https://tidycal.com/team/blank/prima-consulenza-agenti-ai"
                    className="rounded-lg overflow-hidden shadow-inner"
                    style={{
                      width: '100%',
                      height: '700px',
                      border: '2px solid #e5e7eb',
                      backgroundColor: '#f9fafb'
                    }}
                    title="Prenota una consulenza strategica"
                    loading="lazy"
                    onError={() => setIframeError(true)}
                  />
                ) : (
                  /* Fallback - mostra questo se l'iframe non carica */
                  <div className="flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12" style={{ minHeight: '700px' }}>
                    <div className="text-center max-w-lg">
                      <CalendarIcon className="h-16 w-16 text-primary-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        Configurazione TidyCal in Corso
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Il calendario di prenotazione verrà attivato a breve.
                      </p>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                        <p className="text-sm font-semibold text-blue-900 mb-2">Per configurare TidyCal:</p>
                        <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                          <li>Accedi a <a href="https://tidycal.com" target="_blank" rel="noopener noreferrer" className="underline">TidyCal.com</a></li>
                          <li>Crea un evento chiamato &quot;Prima Consulenza Agenti AI&quot;</li>
                          <li>Copia il link di booking</li>
                          <li>Aggiorna il link nell&apos;iframe alla riga 119 di BookingPage.tsx</li>
                        </ol>
                        <p className="text-xs text-blue-700 mt-3">
                          Link attuale: <code className="bg-blue-100 px-2 py-0.5 rounded">https://tidycal.com/team/blank/prima-consulenza-agenti-ai</code>
                        </p>
                      </div>
                      <div className="mt-6">
                        <a
                          href="mailto:info@blankgrowth.agency"
                          className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                        >
                          Contattaci via Email
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="bg-gradient-to-r from-primary-600 to-blue-600 rounded-lg p-8 text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-3">
              Hai Domande?
            </h3>
            <p className="text-primary-100 mb-6">
              Contattaci direttamente per qualsiasi informazione
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:info@blankgrowth.agency"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-600 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                info@blankgrowth.agency
              </a>
              <a
                href="https://blankgrowth.agency"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-700 text-white rounded-lg font-medium hover:bg-primary-800 transition-colors border border-white/20"
              >
                Visita il Sito
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
